import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { ScheduleResponse, ClassBooking } from '../interfaces/schedule.interface';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {
  private readonly CACHE_KEY = 'user_schedule';
  private readonly CACHE_TIMESTAMP_KEY = 'schedule_fetch_timestamp';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

  constructor(
    private http: HttpClient,
    private apiService: ApiService
  ) {}

  fetchSchedule(startDate?: Date, endDate?: Date): Observable<ScheduleResponse> {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      return of({ items: { classes: [], appointments: null } });
    }

    // If dates are provided, bypass cache and make a fresh request
    if (startDate && endDate) {
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${token}`
      });

      const params = {
        start_date: this.formatDate(startDate),
        end_date: this.formatDate(endDate)
      };

      return this.http.get<ScheduleResponse>(
        `${this.apiService.getBaseApiUrl()}/clients/schedule`,
        { headers, params }
      );
    }

    // For requests without dates, use cache logic
    const cachedData = this.getCachedSchedule();
    if (cachedData) {
      return of(cachedData);
    }

    // Default date range for cached data
    const defaultStart = new Date();
    const defaultEnd = new Date();
    defaultEnd.setDate(defaultEnd.getDate() + 7);

    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    const params = {
      start_date: this.formatDate(defaultStart),
      end_date: this.formatDate(defaultEnd)
    };

    return this.http.get<ScheduleResponse>(
      `${this.apiService.getBaseApiUrl()}/clients/schedule`,
      { headers, params }
    ).pipe(
      tap(response => this.cacheSchedule(response))
    );
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private cacheSchedule(response: ScheduleResponse): void {
    localStorage.setItem(this.CACHE_KEY, JSON.stringify(response));
    localStorage.setItem(this.CACHE_TIMESTAMP_KEY, Date.now().toString());
  }

  private getCachedSchedule(): ScheduleResponse | null {
    const timestamp = localStorage.getItem(this.CACHE_TIMESTAMP_KEY);
    const cachedData = localStorage.getItem(this.CACHE_KEY);

    if (!timestamp || !cachedData) {
      return null;
    }

    const age = Date.now() - parseInt(timestamp);
    if (age > this.CACHE_DURATION) {
      // Cache is too old, clear it
      localStorage.removeItem(this.CACHE_KEY);
      localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
      return null;
    }

    return JSON.parse(cachedData);
  }

  getBookedClassIds(): string[] {
    const cachedData = this.getCachedSchedule();
    if (!cachedData || !cachedData.items.classes) {
      return [];
    }
    return cachedData.items.classes
      .filter(booking => booking.status !== 'Cancelled')
      .map(booking => booking.class_details.uuid);
  }

  clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY);
    localStorage.removeItem(this.CACHE_TIMESTAMP_KEY);
  }
} 